import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { NotesService } from '../services/notesService';
import { logger } from '@/common/services/config';
import type {
  ApiGradeFilter,
  ApiAcademicSemesterCourse,
  ApiCourse,
  ApiGrade,
} from '../types/notesTypes';

export const useNotesStore = defineStore('notes', () => {
  // Constants
  const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

  // State
  const semesters = ref<ApiAcademicSemesterCourse[]>([]);
  const periods = ref<ApiGradeFilter[]>([]);
  const currentSemester = ref<ApiAcademicSemesterCourse | null>(null);
  const currentPeriodIndex = ref<number>(0);
  const isLoading = ref<boolean>(false);
  const lastFetchedAt = ref<number | null>(null);

  // Getters
  const isPeriodCharged = computed(() => lastFetchedAt.value !== null);

  const isCacheExpired = computed(() => {
    if (!lastFetchedAt.value) return true;
    return Date.now() - lastFetchedAt.value > CACHE_TTL_MS;
  });
  const currentPeriod = computed(() => periods.value[currentPeriodIndex.value]);
  const sortedCourses = computed(
    () =>
      currentSemester.value?.courses?.sort((a, b) => a.courseLabel.localeCompare(b.courseLabel)) ||
      []
  );

  // Actions
  const fetchAllGrades = async () => {
    try {
      isLoading.value = true;
      const response = await NotesService.getGrades();

      // Sort periods by code (most recent first)
      const sortedPeriods = [...response.gradesFilters].sort((a, b) =>
        b.code.localeCompare(a.code)
      );

      // Sort semesters to match the period order
      const sortedSemesters = sortedPeriods
        .map((period) => response.academicSemesterCourses.find((s) => s.code === period.code))
        .filter((s) => s !== undefined);

      // Store data directly from API
      semesters.value = sortedSemesters;
      periods.value = sortedPeriods;

      // Set the first semester as current (most recent)
      if (sortedSemesters.length > 0) {
        currentSemester.value = sortedSemesters[0];
      }

      lastFetchedAt.value = Date.now();
    } catch (error) {
      logger.error('Error fetching grades:', error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const markNewGradesAsRead = async (semester: ApiAcademicSemesterCourse) => {
    const newGrades = getAllGrades(semester).filter((grade) => !grade.read);
    if (newGrades.length > 0) {
      // Update local state immediately
      newGrades.forEach((grade) => {
        grade.read = true;
      });

      // TODO: Implement API call to mark scores as read when endpoint is available
      // const gradeIds = newGrades.map(grade => grade.id);
      // await NotesService.markScoresAsRead(gradeIds);
    }
  };

  const getAllGrades = (semester: ApiAcademicSemesterCourse): ApiGrade[] => {
    const grades: ApiGrade[] = [];
    semester.courses.forEach((course) => {
      course.grades.forEach((grade) => {
        // Only include non-average grades
        if (!grade.isAverage && !grade.fullAverage) {
          grades.push(grade);
        }
      });
    });
    return grades;
  };

  const navigateToPeriod = async (index: number) => {
    currentPeriodIndex.value = index;

    // Find the semester by matching the code
    const period = periods.value[index];
    const semester = semesters.value.find((s) => s.code === period.code);
    if (semester) {
      currentSemester.value = semester;
      await markNewGradesAsRead(semester);
    }
  };

  const initializeNotes = async () => {
    if (!isPeriodCharged.value || isCacheExpired.value) {
      await fetchAllGrades();
      if (currentSemester.value) {
        await markNewGradesAsRead(currentSemester.value);
      }
    }
  };

  const forceReloadNotes = async () => {
    // Reset the state to force reload
    lastFetchedAt.value = null;
    currentSemester.value = null;
    periods.value = [];
    semesters.value = [];

    // Reload everything
    await fetchAllGrades();
    if (currentSemester.value) {
      await markNewGradesAsRead(currentSemester.value);
    }
  };

  const reset = () => {
    semesters.value = [];
    periods.value = [];
    currentSemester.value = null;
    currentPeriodIndex.value = 0;
    isLoading.value = false;
    lastFetchedAt.value = null;
  };

  return {
    // State
    semesters,
    periods,
    currentSemester,
    currentPeriodIndex,
    isLoading,
    lastFetchedAt,

    // Getters
    isPeriodCharged,
    isCacheExpired,
    currentPeriod,
    sortedCourses,

    // Actions
    fetchAllGrades,
    getAllGrades,
    markNewGradesAsRead,
    navigateToPeriod,
    initializeNotes,
    forceReloadNotes,
    reset,
  };
});
